import { useState, useEffect, useCallback } from 'react';
import { companyProfileService } from '../services/companyProfileService';
import {
  CompanyBasicInfo,
  CompanyProfile,
  CompanyProfilePermissions,
  UpdateCompanyProfileRequest,
  ValidationErrors,
} from '../types/company-profile';
import { useToast } from './useToast';

interface UseCompanyProfileReturn {
  company: CompanyBasicInfo | null;
  profile: CompanyProfile | null;
  permissions: CompanyProfilePermissions | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  validationErrors: ValidationErrors;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: UpdateCompanyProfileRequest) => Promise<void>;
  clearValidationErrors: () => void;
}

export const useCompanyProfile = (
  companyId: number | null
): UseCompanyProfileReturn => {
  const [company, setCompany] = useState<CompanyBasicInfo | null>(null);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [permissions, setPermissions] =
    useState<CompanyProfilePermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const toast = useToast();

  const fetchProfile = useCallback(async () => {
    if (!companyId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const {
        company: companyData,
        profile: profileData,
        permissions: permissionsData,
      } = await companyProfileService.getCompanyProfile(companyId);

      setCompany(companyData);
      setProfile(profileData);
      setPermissions(permissionsData ?? null);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : '企業プロフィールの取得に失敗しました';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, toast]);

  const updateProfile = useCallback(
    async (data: UpdateCompanyProfileRequest) => {
      if (!companyId) {
        setError('企業IDが見つかりません');
        return;
      }

      try {
        setIsSaving(true);
        setError(null);
        setValidationErrors({});

        // フロントエンドでの基本バリデーション
        const errors: ValidationErrors = {};

        if (data.business_content && data.business_content.length > 500) {
          errors.business_content =
            '事業内容・強みは500文字以内で入力してください';
        }

        if (data.recruiting_message && data.recruiting_message.length > 500) {
          errors.recruiting_message =
            '採用メッセージは500文字以内で入力してください';
        }

        if (data.target_candidates && data.target_candidates.length > 300) {
          errors.target_candidates =
            '求める人材像は300文字以内で入力してください';
        }

        if (data.job_types && data.job_types.length > 5) {
          errors.job_types = '募集職種は5つまで選択できます';
        }

        if (data.logo_url && data.logo_url.length > 500) {
          errors.logo_url = 'ロゴURLは500文字以内で入力してください';
        }

        if (Object.keys(errors).length > 0) {
          setValidationErrors(errors);
          return;
        }

        const result = await companyProfileService.updateCompanyProfile(
          companyId,
          data
        );

        // 更新成功後、最新データを再取得
        await fetchProfile();

        toast.success(result.message);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : '企業プロフィールの更新に失敗しました';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsSaving(false);
      }
    },
    [companyId, fetchProfile, toast]
  );

  const clearValidationErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  useEffect(() => {
    if (companyId) {
      fetchProfile();
    }
  }, [companyId]); // fetchProfileを依存関係から削除

  return {
    company,
    profile,
    permissions,
    isLoading,
    isSaving,
    error,
    validationErrors,
    fetchProfile,
    updateProfile,
    clearValidationErrors,
  };
};
